import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { useLang } from '@/i18n/LanguageContext';
import MaskLines, { EASE } from '@/components/MaskLines';
import PatternGreen from '@/components/PatternGreen';

function Option({ option, onPick }) {
  return (
    <motion.button
      data-testid={`situation-option-${option.key.toLowerCase()}`}
      onClick={() => onPick(option.key)}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: EASE, delay: 0.12 * (parseInt(option.key.charCodeAt(0)) - 65) }}
      className="group flex w-full items-baseline gap-5 border hairline p-5 text-left transition-colors duration-300 hover:border-[var(--pz-green)] md:p-6"
    >
      <span className="font-display text-2xl text-[var(--pz-green)] md:text-3xl">{option.key}</span>
      <span className="text-base text-white/85 transition-colors group-hover:text-white md:text-lg">{option.text}</span>
    </motion.button>
  );
}

export default function SituationGame() {
  const { t } = useLang();
  const s = t.situation;
  const [step, setStep] = useState('intro');
  const [choice, setChoice] = useState(null);

  const pick = (key) => {
    setChoice(key);
    setStep('result');
  };

  return (
    <section id="situation" className="relative px-5 py-28 md:px-10 md:py-36" data-testid="section-situation">
      {/* header */}
      <p className="font-mono-label text-[var(--pz-green)]">{s.kicker}</p>
      <div className="mt-6 flex flex-wrap items-end justify-between gap-6">
        <h2 className="font-display text-[16vw] leading-none md:text-[10vw]" data-testid="situation-title">
          {s.title}
        </h2>
        <p className="font-mono-label pb-2 text-white/55">{s.subtitle}</p>
      </div>
      <h3 className="font-display mt-10 text-[10vw] md:text-[5.5vw] md:leading-[0.92]">
        <MaskLines lines={[s.headlineA, s.headlineB]} />
        <span className="text-[var(--pz-green)]">
          <MaskLines lines={[s.headlineC]} delay={0.2} />
        </span>
      </h3>
      <p className="mt-10 max-w-2xl text-base text-white/65 md:text-lg">{s.intro}</p>

      <ul className="mt-12 grid gap-4 sm:grid-cols-2">
        {s.gives.map((g, i) => (
          <li key={g} className="flex items-baseline gap-4 border-l-2 border-[var(--pz-green)] pl-5" data-testid={`situation-give-${i + 1}`}>
            <span className="font-mono-label text-white/40">{String(i + 1).padStart(2, '0')}</span>
            <span className="font-display text-xl md:text-2xl">{g}</span>
          </li>
        ))}
      </ul>
      <p className="mt-8 max-w-2xl text-base italic text-white/60">{s.noScript}</p>

      {/* interactive part */}
      <div className="relative mt-16 min-h-[380px] md:min-h-[420px]" data-testid="situation-game">
        <AnimatePresence mode="wait">
          {step === 'intro' && (
            <motion.div key="intro" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.4 }}>
              <button
                data-testid="situation-game-start"
                onClick={() => setStep('scene')}
                className="font-display group inline-flex items-center gap-5 border border-[var(--pz-green)] px-10 py-6 text-2xl transition-all duration-300 hover:bg-[var(--pz-green)] hover:text-black md:text-4xl"
              >
                {s.cta}
                <span aria-hidden="true" className="transition-transform duration-300 group-hover:translate-x-2">
                  →
                </span>
              </button>
            </motion.div>
          )}

          {step === 'scene' && (
            <motion.div key="scene" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.5, ease: EASE }} data-testid="situation-scene-card">
              <div className="max-w-3xl border hairline p-6 md:p-10">
                <dl className="grid gap-5 md:grid-cols-3">
                  {[
                    ['RUOLO', s.scene.role],
                    ['CONTESTO', s.scene.context],
                    ['OBIETTIVO', s.scene.goal],
                  ].map(([label, text]) => (
                    <div key={label}>
                      <dt className="font-mono-label text-[var(--pz-green)]">{label}</dt>
                      <dd className="mt-2 text-sm text-white/80 md:text-base">{text}</dd>
                    </div>
                  ))}
                </dl>
                <p className="font-display mt-10 text-3xl md:text-5xl">{s.scene.prompt}</p>
                <div className="mt-8 grid gap-3">
                  {s.scene.options.map((option) => (
                    <Option key={option.key} option={option} onPick={pick} />
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {step === 'result' && (
            <motion.div key="result" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }} data-testid="situation-result" aria-live="polite">
              <div className="relative overflow-hidden border border-[var(--pz-green)]/50">
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.2 }} className="absolute inset-0">
                  <PatternGreen className="absolute inset-0" />
                </motion.div>
                <div className="relative bg-black/70 p-6 backdrop-blur-sm md:p-10">
                  <p className="font-mono-label text-[var(--pz-green)]">
                    {s.result.chosenLabel} {choice} — {s.result.noRight}
                  </p>
                  <p className="font-display mt-8 text-[9vw] leading-[0.9] md:text-[4vw]">
                    <span className="block">{s.result.q1}</span>
                    <span className="block text-[var(--pz-green)]">{s.result.q2}</span>
                  </p>
                  <p className="mt-8 max-w-2xl text-base text-white/75 md:text-lg">{s.result.body}</p>
                  <p className="font-display mt-8 flex flex-wrap gap-x-8 gap-y-3 text-2xl md:text-4xl">
                    {s.result.can.map((c) => (
                      <span key={c} className="text-stroke-green">
                        {c}
                      </span>
                    ))}
                  </p>
                  <button
                    data-testid="situation-retry"
                    onClick={() => {
                      setChoice(null);
                      setStep('scene');
                    }}
                    className="mt-10 inline-flex items-center gap-3 font-mono-label text-white/70 transition-colors hover:text-[var(--pz-green)]"
                  >
                    <ArrowLeft size={16} />
                    {s.result.retry}
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* areas + closing */}
      <div className="mt-24">
        <p className="font-mono-label text-white/45">{s.areasLabel}</p>
        <ul className="mt-6 flex flex-wrap gap-2.5">
          {s.areas.map((area) => (
            <li key={area} className="font-mono-label rounded-full border border-white/25 px-4 py-2 text-white/80">
              {area}
            </li>
          ))}
        </ul>
      </div>
      <div className="mt-24 max-w-5xl">
        <p className="font-display text-[7.5vw] leading-[0.92] md:text-[3.6vw]">
          <MaskLines lines={[s.closeA]} />
          <span className="text-white/55">
            <MaskLines lines={[s.closeB]} delay={0.15} />
          </span>
        </p>
      </div>
    </section>
  );
}
